/**
 * 맥스매치 운영 React Query 훅.
 */
import {
  maxMatchOpsService,
  type MaxMatchOpsListParams,
  type MaxMatchOpsMrPayload,
  type MaxMatchOpsCreateRequest,
} from '@/entities/max-match/api/max-match-ops.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const maxMatchOpsKeys = {
  all: ['max-match-ops'] as const,
  lists: () => [...maxMatchOpsKeys.all, 'list'] as const,
  detail: (summaryId: number) =>
    [...maxMatchOpsKeys.all, 'detail', summaryId] as const,
  images: (summaryId: number) =>
    [...maxMatchOpsKeys.all, 'images', summaryId] as const,
  mrs: (imageId: number) =>
    [...maxMatchOpsKeys.all, 'mrs', imageId] as const,
};

export const useMaxMatchOpsList = (
  search: MaxMatchOpsListParams,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: maxMatchOpsKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      maxMatchOpsService.list(s, p),
  });

export const useMaxMatchOpsDetail = (summaryId: number) =>
  useQuery({
    queryKey: maxMatchOpsKeys.detail(summaryId),
    queryFn: () => maxMatchOpsService.getDetail(summaryId),
    enabled: summaryId > 0,
  });

export const useMaxMatchOpsImages = (summaryId: number) =>
  useQuery({
    queryKey: maxMatchOpsKeys.images(summaryId),
    queryFn: () => maxMatchOpsService.getImages(summaryId),
    enabled: summaryId > 0,
    // imageUrl은 presigned — 캐시하지 않고 매 조회 갱신
    staleTime: 0,
  });

export const useCreateMaxMatchOpsPresignedUrls = () =>
  useMutation({
    mutationFn: (filenames: string[]) =>
      maxMatchOpsService.createPresignedUrls(filenames),
  });

export const useCreateMaxMatchOps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: MaxMatchOpsCreateRequest) =>
      maxMatchOpsService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchOpsKeys.lists() });
    },
  });
};

/** MR 변경 후 이미지 확정이 해제되므로 images + mrs 함께 invalidate */
const invalidateOpsImageState = (
  queryClient: ReturnType<typeof useQueryClient>,
  summaryId: number,
  imageId: number,
) => {
  queryClient.invalidateQueries({
    queryKey: maxMatchOpsKeys.images(summaryId),
  });
  queryClient.invalidateQueries({
    queryKey: maxMatchOpsKeys.mrs(imageId),
  });
  queryClient.invalidateQueries({
    queryKey: maxMatchOpsKeys.detail(summaryId),
  });
};

export const useAddMaxMatchOpsMr = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      imageId,
      payload,
    }: {
      imageId: number;
      payload: MaxMatchOpsMrPayload;
    }) => maxMatchOpsService.addMr(imageId, payload),
    onSuccess: (_, { imageId }) => {
      invalidateOpsImageState(queryClient, summaryId, imageId);
    },
  });
};

export const useAddMaxMatchOpsMrsBatch = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      imageId,
      payload,
    }: {
      imageId: number;
      payload: MaxMatchOpsMrPayload[];
    }) => maxMatchOpsService.addMrsBatch(imageId, payload),
    onSuccess: (_, { imageId }) => {
      invalidateOpsImageState(queryClient, summaryId, imageId);
    },
  });
};

export const useUpdateMaxMatchOpsMr = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mrId,
      imageId: _imageId,
      payload,
    }: {
      mrId: number;
      imageId: number;
      payload: MaxMatchOpsMrPayload;
    }) => maxMatchOpsService.updateMr(mrId, payload),
    onSuccess: (_, { imageId }) => {
      invalidateOpsImageState(queryClient, summaryId, imageId);
    },
  });
};

export const useDeleteMaxMatchOpsMr = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mrId,
      imageId: _imageId,
    }: {
      mrId: number;
      imageId: number;
    }) => maxMatchOpsService.deleteMr(mrId),
    onSuccess: (_, { imageId }) => {
      invalidateOpsImageState(queryClient, summaryId, imageId);
    },
  });
};

export const useDeleteAllMaxMatchOpsMrs = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => maxMatchOpsService.deleteAllMrs(imageId),
    onSuccess: (_, imageId) => {
      invalidateOpsImageState(queryClient, summaryId, imageId);
    },
  });
};

export const useConfirmMaxMatchOpsImage = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => maxMatchOpsService.confirmImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.images(summaryId),
      });
    },
  });
};

export const useConfirmMaxMatchOpsBatch = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageIds: number[]) =>
      maxMatchOpsService.confirmBatch(imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.images(summaryId),
      });
    },
  });
};

export const useDeleteMaxMatchOpsImage = (summaryId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => maxMatchOpsService.deleteImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.images(summaryId),
      });
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.detail(summaryId),
      });
    },
  });
};

export const useDeleteMaxMatchOpsSummary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (summaryId: number) =>
      maxMatchOpsService.deleteSummary(summaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maxMatchOpsKeys.all });
    },
  });
};

export const useRunMaxMatchOps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (summaryId: number) => maxMatchOpsService.run(summaryId),
    onSuccess: (_, summaryId) => {
      queryClient.invalidateQueries({ queryKey: maxMatchOpsKeys.all });
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.detail(summaryId),
      });
      queryClient.invalidateQueries({
        queryKey: maxMatchOpsKeys.images(summaryId),
      });
    },
  });
};

export const useDownloadMaxMatchOpsExcel = () =>
  useMutation({
    mutationFn: (summaryId: number) =>
      maxMatchOpsService.getExcelDetails(summaryId),
  });
